import React, { FC, useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Form, Modal } from 'antd';
import ModalFormFooter from '../common/ModalFormFooter';
import {
	capexAssociationRestCrudThunksForBuyer,
	capexAssociationRestCrudThunksForSupplier,
} from '../../thunks/capex_association_thunks';
import {
	rmAssociationRestCrudThunksForBuyer,
	rmAssociationRestCrudThunksForSupplier,
} from '../../thunks/rm_association_thunks';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import ExpenseCategoryInput from '../expense_category_input/ExpenseCategoryInput';
import { ROLE_TYPES } from '../../utils/DataConstants';

const CATEGORY_CONFIG = {
	capex: {
		title: 'Add CapEx Category',
		label: 'CapEx Category',
		validationMessage: 'Please select/enter a capEx category!',
		categoryIdField: 'capExCategoryId',
		categoryField: 'capExCategory',
		okText: 'Add Category',
		cancelText: 'Skip',
	},
	rm: {
		title: 'Add R&M Category',
		label: 'R&M Category',
		validationMessage: 'Please select/enter an R&M category!',
		categoryIdField: 'rmCategoryId',
		categoryField: 'rmCategory',
		okText: 'Add Category',
		cancelText: 'Skip',
	},
};

interface ExpenseCategoryAdditionalInfoPopupProps {
	categoryType: 'capex' | 'rm';
	onCancel: any;
	saveAssociation?: any;
	workOrder: any;
	onSuccess: any;
}

const ExpenseCategoryAdditionalInfoPopup: FC<any> = ({
	categoryType,
	onCancel,
	saveAssociation,
	workOrder,
	onSuccess,
}): React.ReactElement => {
	const config = CATEGORY_CONFIG[categoryType];
	const [saving, setSaving] = useState(false);

	const FORM_ID = `${categoryType}-category-form`;

	const handleSubmit = useCallback(
		(values) => {
			setSaving(true);

			const categoryId = nullSafeGet(`name.${config.categoryIdField}`, values);
			const category = nullSafeGet(`name.${config.categoryField}`, values) || values.name;

			saveAssociation({
				workOrderId: workOrder.id,
				...(categoryId && { [config.categoryIdField]: categoryId }),
				[config.categoryField]: category,
			})
				.then((res) => onSuccess(res))
				.finally(() => setSaving(false));
		},
		[onSuccess, saveAssociation, workOrder, config]
	);

	return (
		<Modal visible={true} title={config.title} onCancel={onCancel} footer={null} closable={true}>
			<Form
				id={FORM_ID}
				layout="vertical"
				initialValues={{}}
				requiredMark={false}
				onFinish={handleSubmit}
			>
				<Form.Item
					name="name"
					label={config.label}
					rules={[
						{
							required: true,
							validator: (_, value) => {
								if (!nullSafeGet(config.categoryField, value)) {
									return Promise.reject(config.validationMessage);
								}

								return Promise.resolve();
							},
						},
					]}
					initialValue={{
						[config.categoryIdField]: workOrder[config.categoryIdField],
						[config.categoryField]: workOrder[config.categoryField],
					}}
				>
					<ExpenseCategoryInput categoryType={categoryType} />
				</Form.Item>
				<ModalFormFooter
					okText={config.okText}
					cancelText={config.cancelText}
					loading={saving}
					formId={FORM_ID}
					onCancelClick={onCancel}
				/>
			</Form>
		</Modal>
	);
};

const SAVE_MAP = {
	capex: {
		[ROLE_TYPES.BUYER]: capexAssociationRestCrudThunksForBuyer,
		[ROLE_TYPES.SUPPLIER]: capexAssociationRestCrudThunksForSupplier,
	},
	rm: {
		[ROLE_TYPES.BUYER]: rmAssociationRestCrudThunksForBuyer,
		[ROLE_TYPES.SUPPLIER]: rmAssociationRestCrudThunksForSupplier,
	},
};

const mapDispatchToProps = (dispatch, ownProps) => ({
	saveAssociation: (entity) =>
		dispatch(SAVE_MAP[ownProps.categoryType][ownProps.userType].create(entity)),
});

const ComponentWithoutUserType = withRouter(
	connect(null, mapDispatchToProps)(ExpenseCategoryAdditionalInfoPopup)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
